import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '../../../../../store/index';
import { useTranslation } from 'react-i18next';

import {
  Grid,
  Divider,
  Button,
  useTheme,
  LinearProgress,
  Typography,
  Avatar,
  IconButton,
  CardContent,
  CardHeader,
  Fab,
  Tooltip,
  Card,
  CardActions,
  useMediaQuery,
  withStyles,
  styled,
} from '@material-ui/core';
import { useStyles } from './RiskSummary.style';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import SummaryParagraphHeader from '../SummaryParagraphHeader';
import TimelineIcon from '@material-ui/icons/Timeline';
import BubbleChartIcon from '@material-ui/icons/BubbleChart';
import WarningIcon from '@material-ui/icons/Warning';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import BRisk from '../../../../../resources/icons/BRisk.png';
import { Error } from '@material-ui/icons';
import { highDensity } from '../../../../../helpers/density';
import { getRecommendations } from '../../../../../helpers/riskRecommendations';
import { getRiskColor } from '../../../../../helpers/manageColors';

const RiskSummary = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles();
  const { risk_factors, risk_evaluation } = useSelector((state) => state.results);
  const { premium_features } = useSelector((state) => state.global_settings);
  const { pendingReprocessing } = useSelector((state) => state.examinations);

  const [riskSourceSelected, setRiskSourceSelected] = useState('NICE');

  const { diagnostics_microcalc_module, diagnostics_opacities_module } = premium_features;
  const { age } = props;

  const { darkState, examinationListDrawer } = useSelector((store) => store.gui);
  const smallScreen = useMediaQuery('(max-width: 1400px)') || examinationListDrawer;
  const riskSegments = [1.338, 2.362, 4];
  const cohortMax = 4 * risk_evaluation?.cohort_five_year_risk || 8;

  // Actions
  const openReport = () => {
    dispatch(actions.setActiveDialog('risk_management'));
  };
  const openForm = () => {
    dispatch(actions.setActiveDialog('risk_management_form'));
  };

  // Patient Recommendations and Risk information
  const patientRecommendations = (riskSourceSelected) => {
    let recommendations = null;

    if (!risk_evaluation?.risk_level) {
      recommendations = {
        error: 'level_not_eval',
      };
    } else {
      recommendations = getRecommendations(risk_factors, risk_evaluation);
    }

    return Object.entries(recommendations).map(([key, val], i) => {
      if (!val) return null;

      const recommendation = t(`risk_management.risk_recommendations.${val}`);

      return (
        <div className={classes.recommendations} key={i}>
          <Typography
            key={key}
            className={classes.recomendation_text}
            style={{ color: darkState ? '#ffffffb3' : 'gray' }}
          >
            {key === riskSourceSelected && recommendation}
          </Typography>
        </div>
      );
    });
  };

  const patientLifetimeRisk = () => {
    if (!risk_evaluation?.risk_level) {
      return <Typography className={classes.patient_risk_title}>{t('risk_management.no_risk')}</Typography>;
    }

    const riskConfig = {
      low: {
        icon: BubbleChartIcon,
        translationKey: 'risk_management.normal_average',
      },
      moderate: {
        icon: TimelineIcon,
        translationKey: 'risk_management.sligh_high_average',
      },
      high: {
        icon: WarningIcon,
        translationKey: 'risk_management.high_average',
      },
    };

    // Get current risk configuration or default to low
    const currentRisk = riskConfig[risk_evaluation?.risk_level] || riskConfig.low;
    const IconComponent = currentRisk.icon;

    return (
      <div className={classes.tooltip_risk_container}>
        <div className={classes.draggable_risk_info}>
          <Typography className={classes.patient_risk_title}>{t('risk_management.patient_risk')}</Typography>
        </div>

        <div className={classes.lifetime_risk_container}>
          <IconComponent
            style={{ color: getRiskColor(risk_evaluation?.risk_level, theme) }}
            fontSize='large'
          />
          <Typography
            className={`${classes.patient_relative_risk}`}
            style={{ color: getRiskColor(risk_evaluation?.risk_level, theme) }}
          >
            {t(currentRisk.translationKey)}
          </Typography>
        </div>

        {risk_evaluation?.life_time_risk && (
          <Typography className={classes.average_results_text}>
            {t('risk_management.average_results', {
              patient_percentage: risk_evaluation?.life_time_risk.toFixed(1),
            })}
          </Typography>
        )}
      </div>
    );
  };

  // Risk Progress Components
  const getRiskValueColor = (value_type, risk_value, cohort) => {
    if (value_type === 'lifetime') {
      if (risk_value < 17) return `${getRiskColor('low', theme)}95`;
      if (risk_value < 45) return `${getRiskColor('moderate', theme)}95`;
      return `${getRiskColor('high', theme)}95`;
    }

    if (value_type === 'relative') {
      if (risk_value <= 1.338) return `${getRiskColor('low', theme)}95`;
      if (risk_value <= 2.362) return `${getRiskColor('moderate', theme)}95`;
      return `${getRiskColor('high', theme)}95`;
    }

    if (value_type === 'fiveyear') {
      const low = 1.338 * cohort;
      const moderate = 2.362 * cohort;

      if (risk_value <= low) return `${getRiskColor('low', theme)}95`;
      if (risk_value <= moderate) return `${getRiskColor('moderate', theme)}95`;
      return `${getRiskColor('high', theme)}95`;
    }
  };

  const buildSegmentGradient = (segments, max) => {
    const stops = segments.map((s) => (s / max) * 100);

    return `
      linear-gradient(
        to right,
        rgb(179,179,179) 0%,
        rgb(170,170,170) ${stops[0] - 1}%,
        rgb(155,155,155) ${stops[0] + 1}%,
        rgb(140,140,140) ${stops[1] - 1}%,
        rgb(125,125,125) ${stops[1] + 1}%,
        rgb(110,110,110) ${stops[2] - 1}%,
        rgb(97,97,97) ${stops[2] + 1}%,
        rgb(90,90,90) 100%
      )
    `;
  };

  const LifetimeProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 4,
    marginTop: '4px',
    width: '100%',
    background: risk_evaluation
      ? buildSegmentGradient([0, 17, 30], 50) // cutoffs, max = 50
      : 'gray',
    [`& .MuiLinearProgress-bar`]: {
      borderRadius: 0,
      backgroundColor: risk_evaluation
        ? getRiskValueColor('lifetime', risk_evaluation?.life_time_risk)
        : '#4CAF50',
    },
  }));

  const RelativeProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 4,
    marginTop: '4px',
    width: '100%',
    background: risk_evaluation
      ? buildSegmentGradient([1.338, 2.362, 4], 4) // thresholds, max=4
      : 'gray',
    [`& .MuiLinearProgress-bar`]: {
      borderRadius: 0,
      backgroundColor: risk_evaluation ? getRiskValueColor('relative', risk_evaluation?.RR) : '#4CAF50',
    },
  }));

  const FiveYearProgress = styled(LinearProgress)(({ theme, cohort }) => ({
    height: 10,
    borderRadius: 4,
    marginTop: '4px',
    width: '100%',
    background: risk_evaluation?.five_year_risk
      ? buildSegmentGradient(
          [1.338 * cohort, 2.362 * cohort, 4 * cohort], // thresholds
          4 * cohort // max
        )
      : 'gray',
    [`& .MuiLinearProgress-bar`]: {
      borderRadius: 0,
      backgroundColor: risk_evaluation
        ? getRiskValueColor('fiveyear', risk_evaluation?.five_year_risk, cohort)
        : '#4CAF50',
    },
  }));

  return (
    <Card className={classes.card_main_container}>
      <CardHeader
        style={{
          padding: '0',
          height: '38px',
          backgroundColor: theme.palette.texts.generalTitleBackgrounds,
        }}
        title={
          <div
            style={{
              display: 'flex',
              width: '100%',
              padding: '0 8px',
              justifyContent: 'space-between',
            }}
          >
            <SummaryParagraphHeader header={t('b_box.b_risk')} />
          </div>
        }
      />
      <CardContent style={{ padding: '0', height: '65%', overflow: 'auto' }}>
        <div className={classes.risk_assessment_container}>
          <div
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {/* Risk Level */}
            <Tooltip arrow title={<>{patientLifetimeRisk()}</>}>
              <div
                className={
                  risk_evaluation?.risk_level ? classes.risk_content_container : classes.risk_content_empty
                }
                style={{
                  backgroundColor: risk_evaluation?.risk_level
                    ? `${getRiskColor(risk_evaluation?.risk_level, theme)}50`
                    : 'transparent',
                }}
              >
                <Typography
                  className={
                    risk_evaluation?.risk_level
                      ? classes.patient_relative_risk
                      : classes.patient_relative_risk_empty
                  }
                >
                  {risk_evaluation?.risk_level
                    ? t(`risk_management.${risk_evaluation?.risk_level}`)
                    : t(`risk_management.no_risk`)}
                </Typography>
              </div>
            </Tooltip>

            {/* Risk Evaluation Values Cards */}
            <div className={classes.risk_cards_container}>
              {/* Lifetime Risk Card */}
              <Tooltip
                arrow
                title={
                  <Typography className={classes.tooltip_text}>
                    {t('risk_management.lifetime_explanation')}
                  </Typography>
                }
              >
                <div className={classes.risk_card}>
                  <div className={classes.risk_card_content}>
                    <Typography className={classes.risk_card_title}>
                      {t('risk_management.lifetime')}
                    </Typography>
                    <Typography className={classes.risk_card_value}>
                      {risk_evaluation?.life_time_risk
                        ? risk_evaluation?.life_time_risk.toFixed(0) + '%'
                        : 'N/A'}
                    </Typography>
                  </div>

                  <LifetimeProgress
                    variant='determinate'
                    value={
                      risk_evaluation?.life_time_risk
                        ? Math.min((risk_evaluation.life_time_risk / 50) * 100, 100)
                        : 0
                    }
                  />

                  <Typography className={classes.risk_card_explanation}>
                    {t('risk_management.lifetime_subtitle')}
                  </Typography>
                </div>
              </Tooltip>

              {/* Relative Risk Card */}
              <Tooltip
                arrow
                title={
                  <Typography className={classes.tooltip_text}>
                    {t('risk_management.relative_explanation')}
                  </Typography>
                }
              >
                <div className={classes.risk_card}>
                  <div className={classes.risk_card_content}>
                    <Typography className={classes.risk_card_title}>
                      {t('risk_management.relative')}
                    </Typography>
                    <Typography className={classes.risk_card_value}>
                      {risk_evaluation?.RR ? `${risk_evaluation?.RR.toFixed(2)}x` : 'N/A'}
                    </Typography>
                  </div>

                  <RelativeProgress
                    variant='determinate'
                    value={risk_evaluation?.RR ? Math.min((risk_evaluation.RR / 4) * 100, 100) : 0}
                  />
                  <Typography className={classes.risk_card_explanation}>
                    {t('risk_management.relative_subtitle')}
                  </Typography>
                </div>
              </Tooltip>

              {/* Five Year Risk Card */}
              <Tooltip
                arrow
                title={
                  <Typography className={classes.tooltip_text}>
                    {t('risk_management.five_years_explanation')}
                  </Typography>
                }
              >
                <div className={classes.risk_card}>
                  <div className={classes.risk_card_content}>
                    <Typography className={classes.risk_card_title}>
                      {t('risk_management.five_years')}
                    </Typography>
                    <Typography className={classes.risk_card_value}>
                      {risk_evaluation?.five_year_risk
                        ? risk_evaluation?.five_year_risk.toFixed(0) + '%'
                        : 'N/A'}
                    </Typography>
                  </div>

                  <FiveYearProgress
                    variant='determinate'
                    value={
                      risk_evaluation?.five_year_risk
                        ? Math.min(
                            (risk_evaluation.five_year_risk / (4 * risk_evaluation.cohort_five_year_risk)) *
                              100,
                            100
                          )
                        : 0
                    }
                    cohort={risk_evaluation?.cohort_five_year_risk}
                  />

                  <Typography className={classes.risk_card_explanation}>
                    {t('risk_management.5_year_subtitle')}
                  </Typography>
                </div>
              </Tooltip>
            </div>
          </div>
          {/* Risk Evaluation Recommendations */}
          {risk_evaluation ? (
            <>
              <div className={classes.recommendation_container}>
                <Typography className={classes.patient_recommendations_title}>
                  {t('risk_management.recommendations')}
                </Typography>
                <div>
                  <Button
                    onClick={() => setRiskSourceSelected('NICE')}
                    id='NICE'
                    size='small'
                    variant='text'
                    style={{
                      marginRight: '8px',
                      color: riskSourceSelected === 'NICE' ? '#569191' : '#a7a7a7',
                      fontWeight: riskSourceSelected === 'NICE' ? 'bold' : 'normal',
                      textDecoration: riskSourceSelected === 'NICE' ? 'underline' : 'none',
                    }}
                  >
                    NICE
                  </Button>
                  <Button
                    onClick={() => setRiskSourceSelected('BAG')}
                    id='BAG'
                    size='small'
                    variant='text'
                    style={{
                      marginRight: '8px',
                      color: riskSourceSelected === 'BAG' ? '#569191' : '#a7a7a7',
                      fontWeight: riskSourceSelected === 'BAG' ? 'bold' : 'normal',
                      textDecoration: riskSourceSelected === 'BAG' ? 'underline' : 'none',
                    }}
                  >
                    BAG
                  </Button>
                  <Button
                    onClick={() => setRiskSourceSelected('LUKS')}
                    id='LUKS'
                    size='small'
                    variant='text'
                    style={{
                      marginRight: '8px',
                      color: riskSourceSelected === 'LUKS' ? '#569191' : '#a7a7a7',
                      fontWeight: riskSourceSelected === 'LUKS' ? 'bold' : 'normal',
                      textDecoration: riskSourceSelected === 'LUKS' ? 'underline' : 'none',
                    }}
                  >
                    USPSTF
                  </Button>
                </div>
              </div>
              <div className={classes.recommendation_list}>{patientRecommendations(riskSourceSelected)}</div>
            </>
          ) : (
            <div className={classes.risk_content_container_empty}>
              <Typography
                className={classes.risk_info_placeholder}
                style={{
                  lineHeight: 2,
                  color: darkState ? '#ffffffb3' : 'gray',
                }}
              >
                {t('risk_management.patient_risk_disclaimer')}
              </Typography>
            </div>
          )}
        </div>
      </CardContent>

      {/* Risk Actions Buttons */}
      <CardActions className={classes.risk_btn_container}>
        {risk_evaluation ? (
          <Tooltip
            title={
              <Typography className={classes.tooltip_title}>
                {t('risk_management.button_complete')}
              </Typography>
            }
          >
            <span>
              {smallScreen ? (
                <Fab
                  className={classes.summary_fabs}
                  onClick={() => (risk_evaluation !== null ? openReport() : openForm())}
                  disabled={pendingReprocessing}
                >
                  <TrendingUpIcon style={{ color: 'rgb(154 187 223)', height: '16px', width: '16px' }} />
                </Fab>
              ) : (
                <Button
                  disabled={pendingReprocessing}
                  variant='contained'
                  size='small'
                  onClick={() => (risk_evaluation !== null ? openReport() : openForm())}
                  color='primary'
                  style={{ marginLeft: '12px' }}
                  className={classes.actionButton}
                >
                  {t('risk_management.button_complete')}
                </Button>
              )}
            </span>
          </Tooltip>
        ) : (
          <Tooltip
            title={
              <Typography className={classes.tooltip_title}>
                {t('risk_management.button_incomplete')}
              </Typography>
            }
          >
            <span>
              {smallScreen ? (
                <Fab className={classes.summary_fabs} onClick={openForm} disabled={pendingReprocessing}>
                  <NoteAddIcon style={{ color: 'white', height: '16px', width: '16px' }} />
                </Fab>
              ) : (
                <Button
                  disabled={pendingReprocessing}
                  variant='contained'
                  size='small'
                  onClick={openForm}
                  color='primary'
                  style={{ marginLeft: '12px' }}
                  className={classes.actionButton}
                >
                  {t('risk_management.button_incomplete')}
                </Button>
              )}
            </span>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
};

export default RiskSummary;
